import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { IDebtorPaymentRepository } from '@app/domain/ports';
import type { DebtorPayment } from '@app/domain/entities';

interface DebtorPaymentRow {
  id: number;
  debtorId: number;
  date: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  currencyCode: string;
  amount: number;
  exchangeRate: number;
  note: string;
}

export class DebtorPaymentRepository extends BaseSqliteRepository implements IDebtorPaymentRepository {
  private readonly BASE_FROM = `
    FROM debtor_payments dp
    INNER JOIN accounts a ON dp.account_id = a._id
    INNER JOIN currencies c ON dp.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE dp.is_deleted = 0
  `;

  private readonly BASE_FIELDS = [
    'dp._id as id',
    'dp.debtor_id as debtorId',
    'dp.payment_date as date',
    'dp.account_id as accountId',
    'a.name as accountName',
    'cl.ticker as currencyCode',
    'dp.amount as amount',
    'dp.exchange_rate as exchangeRate',
    'dp.note as note',
  ];

  async findByDebtorId(debtorId: number): Promise<DebtorPayment[]> {
    const query = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
        AND dp.debtor_id = ?
      ORDER BY dp.payment_date DESC, dp._id DESC`;

    const rows = await this.query<DebtorPaymentRow>(query, [debtorId]);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async insert(payment: Omit<DebtorPayment, 'id' | 'accountName'>): Promise<number> {
    const date = new Date(payment.date).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO debtor_payments (debtor_id, payment_date, account_id, amount, currency_id, exchange_rate, note)
       VALUES (?, ?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?)
       RETURNING _id AS id`,
      [
        payment.debtorId,
        date,
        payment.accountId,
        payment.amount,
        payment.currencyCode,
        payment.exchangeRate,
        payment.note,
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO debtor_payments', [], 'Failed to insert debtor payment');
    }

    return result.id;
  }

  async update(payment: DebtorPayment): Promise<void> {
    const date = new Date(payment.date).setUTCHours(0, 0, 0, 0) / 1000;

    await this.execute(
      `UPDATE debtor_payments
       SET payment_date = ?,
           account_id = ?,
           amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           exchange_rate = ?,
           note = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [date, payment.accountId, payment.amount, payment.currencyCode, payment.exchangeRate, payment.note, payment.id],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute("UPDATE debtor_payments SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [
      id,
    ]);
  }

  async countByAccount(accountId: number): Promise<number> {
    const result = await this.queryOne<{ count: number }>(
      `SELECT COUNT(*) as count FROM debtor_payments WHERE account_id = ? AND is_deleted = 0`,
      [accountId],
    );
    return result?.count ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE debtor_payments
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  private mapRowToEntity(row: DebtorPaymentRow): DebtorPayment {
    return {
      id: row.id,
      debtorId: row.debtorId,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      currencyCode: row.currencyCode,
      amount: row.amount,
      exchangeRate: !row.exchangeRate ? 1 : row.exchangeRate,
      note: row.note,
    };
  }
}
